// 수열 추리 — 숫자의 규칙을 찾아 빈칸(?)을 채운다. 지능 검사에 꼭 나오는 규칙 찾기 문제.
// 레벨이 오르면 더하기·곱하기 → 번갈아·차이가 커짐 → 자리별로 다른 수열·앞 수의 합 → 제곱·세제곱 → 연산 섞기.
// 한 판은 최근 세 레벨의 유형을 섞고, 틀리면 규칙 설명을 보여 준다.
import { int, makeRng, pick, shuffle, type Rng } from './rng';

export interface SeqQuestion {
  type: string;
  /** null 이 빈칸 */
  terms: (number | null)[];
  answer: number;
  explain: string;
  limitSec: number;
}

type Made = { values: number[]; explain: string };
interface SType { key: string; level: number; make: (rng: Rng) => Made }

export const SEQ_COUNT = 8;

const PRIMES = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41, 43, 47, 53, 59, 61, 67, 71];
const sign = (c: number) => (c > 0 ? `+${c}` : `−${-c}`);

/** 앞 항들로 다음 항을 만드는 수열 */
function grow(first: number[], n: number, next: (v: number[], i: number) => number): number[] {
  const v = [...first];
  while (v.length < n) v.push(next(v, v.length));
  return v;
}

const TYPES: SType[] = [
  {
    key: 'arith', level: 1, make: (r) => {
      const a = int(r, 1, 30), d = int(r, 2, 9);
      return { values: grow([a], 6, (v) => v[v.length - 1] + d), explain: `${d}씩 더합니다` };
    },
  },
  {
    key: 'arithDown', level: 1, make: (r) => {
      const d = int(r, 2, 9), a = int(r, d * 6 + 1, d * 6 + 40);
      return { values: grow([a], 6, (v) => v[v.length - 1] - d), explain: `${d}씩 뺍니다` };
    },
  },
  {
    key: 'geo', level: 2, make: (r) => {
      const ratio = pick(r, [2, 3]), a = ratio === 2 ? int(r, 1, 6) : int(r, 1, 3);
      return { values: grow([a], ratio === 2 ? 6 : 5, (v) => v[v.length - 1] * ratio), explain: `${ratio}배씩 커집니다 (×${ratio})` };
    },
  },
  {
    key: 'divide', level: 2, make: (r) => {
      const ratio = pick(r, [2, 3]), a = int(r, 1, 5) * ratio ** 4;
      return { values: grow([a], 5, (v) => v[v.length - 1] / ratio), explain: `${ratio}로 나눕니다 (÷${ratio})` };
    },
  },
  {
    key: 'alt2', level: 3, make: (r) => {
      const a = int(r, 1, 9), b = pick(r, [1, 2, 3, 4, 5, 6, 7, 8, 9].filter((x) => x !== a));
      return { values: grow([int(r, 1, 20)], 7, (v, i) => v[i - 1] + (i % 2 ? a : b)), explain: `+${a}, +${b}를 번갈아 합니다` };
    },
  },
  {
    key: 'altOps', level: 3, make: (r) => {
      const a = int(r, 4, 12), b = int(r, 1, a - 1);
      return { values: grow([int(r, 1, 20)], 7, (v, i) => v[i - 1] + (i % 2 ? a : -b)), explain: `+${a}, −${b}를 번갈아 합니다` };
    },
  },
  {
    key: 'diffGrow', level: 4, make: (r) => {
      const d0 = int(r, 1, 5), k = int(r, 1, 3);
      return {
        values: grow([int(r, 1, 10)], 6, (v, i) => v[i - 1] + d0 + k * (i - 1)),
        explain: `이웃한 수의 차이가 ${d0}, ${d0 + k}, ${d0 + 2 * k}…로 ${k}씩 커집니다`,
      };
    },
  },
  {
    key: 'altMulAdd', level: 4, make: (r) => {
      const c = int(r, 1, 5);
      return { values: grow([int(r, 1, 5)], 6, (v, i) => (i % 2 ? v[i - 1] * 2 : v[i - 1] + c)), explain: `×2, +${c}를 번갈아 합니다` };
    },
  },
  {
    key: 'interleave', level: 5, make: (r) => {
      const s1 = int(r, 1, 10), a = int(r, 2, 6), s2 = int(r, 20, 40), b = int(r, 1, 4);
      return {
        values: Array.from({ length: 8 }, (_, i) => (i % 2 === 0 ? s1 + a * (i / 2) : s2 - b * ((i - 1) / 2))),
        explain: `두 수열이 섞여 있습니다: 1·3·5·7번째는 +${a}씩, 2·4·6·8번째는 −${b}씩`,
      };
    },
  },
  {
    key: 'cycle3', level: 5, make: (r) => {
      const [a, b, c] = shuffle(r, [1, 2, 3, 4, 5, 6]).slice(0, 3);
      return { values: grow([int(r, 1, 10)], 8, (v, i) => v[i - 1] + [a, b, c][(i - 1) % 3]), explain: `+${a}, +${b}, +${c}를 되풀이합니다` };
    },
  },
  {
    key: 'fib', level: 6, make: (r) => {
      const a = int(r, 1, 5);
      return { values: grow([a, int(r, a, 8)], 7, (v, i) => v[i - 1] + v[i - 2]), explain: '앞의 두 수를 더하면 다음 수가 됩니다' };
    },
  },
  {
    key: 'primes', level: 6, make: (r) => {
      const s = int(r, 0, 8);
      return { values: PRIMES.slice(s, s + 6), explain: '소수(1과 자기 자신으로만 나누어지는 수)를 차례로 늘어놓았습니다' };
    },
  },
  {
    key: 'squares', level: 7, make: (r) => {
      const k = int(r, 1, 7), c = pick(r, [0, 0, 1, -1, 2, 3]);
      const explain = c === 0 ? `제곱수입니다 (${k}², ${k + 1}², ${k + 2}²…)` : `제곱수(${k}², ${k + 1}², ${k + 2}²…)에 ${sign(c)} 한 수입니다`;
      return { values: Array.from({ length: 6 }, (_, i) => (k + i) ** 2 + c), explain };
    },
  },
  {
    key: 'cubes', level: 7, make: (r) => {
      const k = int(r, 1, 4);
      return { values: Array.from({ length: 5 }, (_, i) => (k + i) ** 3), explain: `세제곱수입니다 (${k}³, ${k + 1}³, ${k + 2}³…)` };
    },
  },
  {
    key: 'diffGeo', level: 8, make: (r) => {
      const d0 = int(r, 1, 3), ratio = pick(r, [2, 3]);
      return {
        values: grow([int(r, 1, 10)], 6, (v, i) => v[i - 1] + d0 * ratio ** (i - 1)),
        explain: `이웃한 수의 차이가 ${d0}, ${d0 * ratio}, ${d0 * ratio ** 2}…로 ${ratio}배씩 커집니다`,
      };
    },
  },
  {
    key: 'mulInc', level: 8, make: (r) => ({
      values: grow([int(r, 1, 3)], 5, (v, i) => v[i - 1] * (i + 1)),
      explain: '×2, ×3, ×4…처럼 곱하는 수가 1씩 커집니다',
    }),
  },
  {
    key: 'affine', level: 9, make: (r) => {
      const m = pick(r, [2, 3]), c = pick(r, [-3, -2, -1, 1, 2, 3]);
      const start = c < 0 ? int(r, 4, 7) : int(r, 2, 5);
      return { values: grow([start], m === 2 ? 6 : 5, (v) => v[v.length - 1] * m + c), explain: `×${m} 한 뒤 ${sign(c)} 합니다` };
    },
  },
  {
    key: 'interleaveGeo', level: 9, make: (r) => {
      const s1 = int(r, 1, 4), s2 = int(r, 1, 15), a = int(r, 2, 7);
      return {
        values: Array.from({ length: 8 }, (_, i) => (i % 2 === 0 ? s1 * 2 ** (i / 2) : s2 + a * ((i - 1) / 2))),
        explain: `두 수열이 섞여 있습니다: 1·3·5·7번째는 ×2씩, 2·4·6·8번째는 +${a}씩`,
      };
    },
  },
  {
    key: 'diffSquares', level: 10, make: (r) => {
      const k = int(r, 1, 3);
      return {
        values: grow([int(r, 1, 10)], 6, (v, i) => v[i - 1] + (k + i - 1) ** 2),
        explain: `이웃한 수의 차이가 ${k}², ${k + 1}², ${k + 2}²… 제곱수입니다`,
      };
    },
  },
  {
    key: 'trib', level: 10, make: (r) => ({
      values: grow([int(r, 1, 3), int(r, 1, 3), int(r, 1, 3)], 7, (v, i) => v[i - 1] + v[i - 2] + v[i - 3]),
      explain: '앞의 세 수를 더하면 다음 수가 됩니다',
    }),
  },
];

export const SEQ_TYPES = TYPES.map((t) => t.key);

export function makeSeqRound(level: number, seed: number): SeqQuestion[] {
  const rng = makeRng(seed);
  const lv = Math.min(10, Math.max(1, level));
  const pool = TYPES.filter((t) => t.level <= lv && t.level >= lv - 2);
  const limitSec = lv <= 4 ? 30 : lv <= 7 ? 45 : 60;
  const out: SeqQuestion[] = [];
  let bag: SType[] = [];
  while (out.length < SEQ_COUNT) {
    if (bag.length === 0) bag = shuffle(rng, pool);
    const t = bag.pop()!;
    const { values, explain } = t.make(rng);
    // 빈칸은 보통 맨 끝. 레벨 5부터는 가끔 중간 (앞뒤를 같이 봐야 풀림)
    const blank = lv >= 5 && rng() < 0.3 ? int(rng, 2, values.length - 2) : values.length - 1;
    const terms = values.map((v, i) => (i === blank ? null : v));
    if (out.some((q) => JSON.stringify(q.terms) === JSON.stringify(terms))) continue;
    out.push({ type: t.key, terms, answer: values[blank], explain, limitSec });
  }
  return out;
}
